import { useMemo, useRef } from 'react'
import { EventInput } from '@fullcalendar/core'
import { useTranslation } from 'react-i18next'

import {
  Events,
  SubEvents,
  Sessions,
  SubEventsBase,
  Contributors,
  SubEventType,
  ContributorRole,
} from '../../../../types'
import {
  useSubEventsTypeOptions,
  useTagsDataOptions,
  useLocationDataOptions,
  useRoomsDataOptions,
  compareHours,
} from '../../../../utils'

export interface ExtendedProps {
  subEventId: number | number[]
  sessionId: number[] | number
  description?: string | null
  location?: number | null
  salles?: ReturnType<typeof useRoomsDataOptions>
  etablissement?: string | null
  type?: string[]
  contributors?: Contributors[]
  isSession?: boolean
  isDetail?: boolean
  sessions: {
    shortName?: string | null
    color?: string | null
    hide_from_generic_program: boolean
    tags?: string[]
  }[]
}

export type CalendarData =
  | {
      event: Events
      sessions: Sessions[]
      subEvents: SubEvents[]
    }
  | null
  | undefined

export const useCalendarData = (
  data: CalendarData,
  params: {
    showWithoutSession: boolean
    showHotel: boolean
    showHiddenFromGenericProgram: boolean
  }
) => {
  const { showWithoutSession, showHotel, showHiddenFromGenericProgram } = params
  const { t, i18n } = useTranslation()
  const subEventsTypeOptions = useSubEventsTypeOptions(t)
  const tagsOptions = useTagsDataOptions()
  const locationsOptions = useLocationDataOptions()
  const roomsOptions = useRoomsDataOptions(data?.event.location, locationsOptions)

  const slotMinTime = useRef<string | undefined>(data?.event.start)
  const slotMaxTime = useRef<string | undefined>(data?.event.end)

  const resources = useMemo<{ id: string; title: string; groupId?: string }[]>(() => [], [])

  const sessions = useMemo(() => {
    if (!data) return undefined

    const parsed: (EventInput & { resourcesId?: string[] })[] = []
    data.sessions.forEach(session => {
      if (!showHiddenFromGenericProgram && session.hide_from_generic_program) return

      if (slotMinTime.current && slotMaxTime.current) {
        slotMinTime.current =
          compareHours(session.start, slotMinTime.current) === -1
            ? session.start
            : slotMinTime.current
        slotMaxTime.current =
          compareHours(session.end, slotMaxTime.current) === 1 ? session.end : slotMaxTime.current
      } else {
        slotMinTime.current = session.start
        slotMaxTime.current = session.end
      }
      const salles =
        session.rooms
          ?.map(room => roomsOptions.find(availableRoom => availableRoom.id === room))
          .filter(el => el !== undefined) ?? []
      // salles.forEach(el => {
      //   resources.push({
      //     id: el.id.toString(),
      //     title: el.name,
      //     groupId: el.groupId.toString(),
      //   })
      // })
      const tags =
        session.tags
          ?.map(tag => {
            const opt = tagsOptions.find(option => option.value === tag)

            if (opt) {
              resources.push({
                id: opt.value.toString(),
                title: opt.label,
              })
            }
            return opt
          })
          .filter(el => el !== undefined) ?? []
      parsed.push({
        title: session.short_name || session.name,
        start: session.start,
        end: session.end,
        isSession: true,
        resourceIds: tags.map(el => el.value.toString()),
        borderColor: session.color || '#000000',
        extendedProps: {
          sessionId: session.id,
          subEventId: session.sub_events,
          location: data.event.location,
          salles,
          description: session.description,
          type: tags?.map(tag => tag?.label ?? '') ?? [],
          contributors:
            // Participants are never displayed in the calendar
            session.contributors?.filter(
              co => !co.roles?.some(role => role === ContributorRole.Participant)
            ) ?? [],
          etablissement: undefined,
          // isDetail: true,
          sessions: [],
        } satisfies ExtendedProps,
      })
    })
    return parsed
  }, [data, resources, roomsOptions, showHiddenFromGenericProgram, tagsOptions])

  const events = useMemo(() => {
    const parsed: (EventInput & { resourcesId?: string[] })[] = []
    if (!data) return undefined

    data.subEvents.forEach(_subEvent => {
      // Hide subEvent conditions
      if (!showHiddenFromGenericProgram && _subEvent.hide_from_generic_program) return
      if (!showWithoutSession && _subEvent.sessions.length === 0) return
      if (!showHotel && _subEvent.sub_event_type === SubEventType.HotelSubEvent) return

      const sessions = _subEvent.sessions.reduce(
        (acc, sessionId) => {
          const el = data.sessions.find(session => session.id === sessionId)
          if (el) {
            acc.push({
              shortName: el.short_name,
              color: el.color,
              hide_from_generic_program: !!el.hide_from_generic_program,
              tags:
                el.tags?.map(
                  tag => tagsOptions.find(option => option.value === tag)?.label ?? ''
                ) ?? [],
            })
          }
          return acc
        },
        [] as ExtendedProps['sessions']
      )

      // Hide subEvent if all sessions are hidden
      if (!showHiddenFromGenericProgram && sessions.length > 0) {
        const isAllSessionsHidden = sessions.every(
          session => session.hide_from_generic_program === true
        )
        if (isAllSessionsHidden) {
          return
        }
      }

      const subEvent = _subEvent as SubEventsBase
      if (slotMinTime.current && slotMaxTime.current) {
        slotMinTime.current =
          compareHours(subEvent.start, slotMinTime.current) === -1
            ? subEvent.start
            : slotMinTime.current
        slotMaxTime.current =
          compareHours(subEvent.end, slotMaxTime.current) === 1 ? subEvent.end : slotMaxTime.current
      } else {
        slotMinTime.current = subEvent.start
        slotMaxTime.current = subEvent.end
      }

      const salles =
        subEvent.rooms
          ?.map(room => roomsOptions.find(availableRoom => availableRoom.id === room))
          .filter(el => el !== undefined) ?? []

      parsed.push({
        title: subEvent.name,
        start: subEvent.start,
        end: subEvent.end,
        resourceIds: salles.map(el => el.id.toString()),
        borderColor:
          subEventsTypeOptions.find(option => option.value === subEvent.sub_event_type)?.color ??
          '#000000',
        extendedProps: {
          subEventId: subEvent.id,
          sessionId: subEvent.sessions,
          location: data.event.location,
          salles,
          description: subEvent.description,
          sessions: sessions,
          type:
            subEvent.tags?.map(
              tag => tagsOptions.find(option => option.value === tag)?.label ?? ''
            ) ?? [],
          contributors:
            // Participants are never displayed in the calendar
            subEvent.contributors?.filter(
              co => !co.roles?.some(role => role === ContributorRole.Participant)
            ) ?? [],
          etablissement: undefined,
        } satisfies ExtendedProps,
      })
    })

    return parsed
  }, [
    data,
    // resources,
    roomsOptions,
    showHiddenFromGenericProgram,
    showHotel,
    showWithoutSession,
    subEventsTypeOptions,
    tagsOptions,
  ])

  return {
    events,
    sessions,
    resources,
    slotMinTime: slotMinTime.current
      ? `${new Date(slotMinTime.current).getHours().toLocaleString(i18n.resolvedLanguage, { minimumIntegerDigits: 2 })}:${new Date(slotMinTime.current).getMinutes().toLocaleString(i18n.resolvedLanguage, { minimumIntegerDigits: 2 })}:00`
      : '08:00:00',
    slotMaxTime: slotMaxTime.current
      ? `${new Date(slotMaxTime.current).getHours().toLocaleString(i18n.resolvedLanguage, { minimumIntegerDigits: 2 })}:${new Date(slotMaxTime.current).getMinutes().toLocaleString(i18n.resolvedLanguage, { minimumIntegerDigits: 2 })}:00`
      : '18:00:00',
  }
}

export type ReturnUseCalendarData = ReturnType<typeof useCalendarData>
